/**
 * m3 echo Frontend - Settings Page
 *
 * Einstellungsseite für Prompts.
 * KI-Modelle und Admin-only tabs wurden nach Admin Dashboard verschoben.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
  useMediaQuery,
  useTheme,
  Alert,
  Collapse,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { promptsApi } from '../services/api';
import { Prompt } from '../types/api';
import { useUIStore } from '../stores/useUIStore';
import { useAuthStore } from '../stores/useAuthStore';
import { tenantApi, TenantOption } from '../services/admin';
import AnimatedTabPanel from '../components/AnimatedTabPanel';
import { usePromptQualityWarning } from '../hooks/usePromptQualityWarning';
import { analyzePromptQuality } from '../utils/promptQuality';

const Settings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Use Zustand store for tab persistence
  const { settingsActiveTab, setSettingsTab } = useUIStore();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.is_admin || false;
  const [prompts, setPrompts] = useState<Prompt[]>([]);

  const tabs = [
    { label: 'Prompts', key: 'prompts' },
  ];
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newPrompt, setNewPrompt] = useState('');
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [editPromptContent, setEditPromptContent] = useState('');
  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);

  // Prompt quality warnings
  const newPromptWarning = usePromptQualityWarning(newPrompt);
  const editPromptWarning = usePromptQualityWarning(editPromptContent);

  // Tenant selector state (for admin users)
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  /**
   * Lädt Einstellungsdaten beim Mounten
   */
  useEffect(() => {
    // For admin users, wait for tenant selection before loading prompts
    // For regular users, load immediately
    if (!isAdmin) {
      loadPrompts();
    }
  }, []);

  /**
   * Reset active tab if current tab is not available
   */
  useEffect(() => {
    if (settingsActiveTab >= tabs.length) {
      setSettingsTab(0);
    }
  }, [settingsActiveTab, setSettingsTab]);

  /**
   * Lädt Tenants für Admin-Benutzer beim Mounten
   */
  useEffect(() => {
    if (isAdmin) {
      loadTenants();
    }
  }, [isAdmin]);

  /**
   * Lädt Prompts neu wenn Tenant geändert wird (nur für Admin)
   */
  useEffect(() => {
    if (isAdmin && selectedTenantId) {
      loadPrompts();
    }
  }, [selectedTenantId]);

  /**
   * Lädt Tenants für Admin-Benutzer
   */
  const loadTenants = async () => {
    if (!isAdmin) return;

    try {
      setTenantsLoading(true);
      const response = await tenantApi.getOptions();
      setTenants(response.data);
      // Auto-select first tenant if available
      if (response.data.length > 0 && selectedTenantId === null) {
        setSelectedTenantId(response.data[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Mandanten:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  /**
   * Lädt alle Prompts von der API
   */
  const loadPrompts = async () => {
    try {
      const params = isAdmin && selectedTenantId ? { tenant_id: selectedTenantId } : {};
      const response = await promptsApi.getAll(params);
      setPrompts(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Prompts:', error);
    }
  };

  /**
   * Erstellt einen neuen Prompt
   */
  const handleCreatePrompt = async () => {
    try {
      const tenant_id = isAdmin && selectedTenantId ? selectedTenantId : undefined;
      await promptsApi.create({ content: newPrompt }, tenant_id);
      setNewPrompt('');
      setOpenCreateDialog(false);
      loadPrompts();
    } catch (error) {
      console.error('Fehler beim Erstellen des Prompts:', error);
    }
  };

  /**
   * Öffnet den Edit-Dialog für einen Prompt
   */
  const handleOpenEditDialog = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setEditPromptContent(prompt.content);
    setOpenEditDialog(true);
  };

  /**
   * Aktualisiert einen Prompt
   */
  const handleUpdatePrompt = async () => {
    if (!editingPrompt) return;

    try {
      await promptsApi.update(editingPrompt.id, { content: editPromptContent });
      setOpenEditDialog(false);
      setEditingPrompt(null);
      setEditPromptContent('');
      loadPrompts();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Prompts:', error);
    }
  };

  /**
   * Öffnet den Delete-Dialog
   */
  const handleOpenDeleteDialog = (prompt: Prompt) => {
    setPromptToDelete(prompt);
    setOpenDeleteDialog(true);
  };

  /**
   * Löscht einen Prompt (nach Bestätigung)
   */
  const handleConfirmDelete = async () => {
    if (!promptToDelete) return;

    try {
      await promptsApi.delete(promptToDelete.id);
      setOpenDeleteDialog(false);
      setPromptToDelete(null);
      loadPrompts();
    } catch (error) {
      console.error('Fehler beim Löschen des Prompts:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Einstellungen
      </Typography>

      <Paper>
        <Tabs value={settingsActiveTab} onChange={(_, newValue) => setSettingsTab(newValue)} variant={isMobile ? 'fullWidth' : 'scrollable'} scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>

        {/* Prompts Tab */}
        <AnimatedTabPanel value={settingsActiveTab} index={0}>
          {isAdmin && (
            <TextField
              select
              fullWidth
              label="Mandant auswählen"
              value={selectedTenantId || ''}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              disabled={tenantsLoading}
              helperText="Wählen Sie einen Mandanten aus, um dessen Prompts anzuzeigen"
              sx={{ mb: 3 }}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.display_name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Prompt-Verwaltung</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
            >
              Neuer Prompt
            </Button>
          </Box>

          <List>
            {prompts.map((prompt) => (
              <ListItem
                key={prompt.id}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      sx={{ mr: 1 }}
                      onClick={() => handleOpenEditDialog(prompt)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleOpenDeleteDialog(prompt)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{prompt.content}</span>
                      {analyzePromptQuality(prompt.content) && (
                        <Tooltip title="Dieser Prompt ist eventuell zu kurz" arrow>
                          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.1rem', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={`Platzhalter: ${prompt.placeholders.join(', ') || 'Keine'}`}
                />
              </ListItem>
            ))}
          </List>
        </AnimatedTabPanel>

      </Paper>

      {/* Create Prompt Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} fullScreen={isMobile}>
        <DialogTitle>Neuen Prompt erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Prompt-Inhalt"
            fullWidth
            multiline
            rows={4}
            value={newPrompt}
            onChange={(e) => setNewPrompt(e.target.value.slice(0, 300))}
            helperText={`Verwenden Sie [platzhalter] für dynamische Werte (${newPrompt.length}/300 Zeichen)`}
            error={newPrompt.length >= 300}
            inputProps={{ maxLength: 300 }}
          />
          <Collapse in={!!newPromptWarning}>
            <Alert severity="warning" sx={{ mt: 1 }}>{newPromptWarning}</Alert>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Abbrechen</Button>
          <Button onClick={handleCreatePrompt} variant="contained" disabled={newPrompt.length > 300}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Prompt Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullScreen={isMobile}>
        <DialogTitle>Prompt bearbeiten</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Prompt-Inhalt"
            fullWidth
            multiline
            rows={4}
            value={editPromptContent}
            onChange={(e) => setEditPromptContent(e.target.value.slice(0, 300))}
            helperText={`Verwenden Sie [platzhalter] für dynamische Werte (${editPromptContent.length}/300 Zeichen)`}
            error={editPromptContent.length >= 300}
            inputProps={{ maxLength: 300 }}
          />
          <Collapse in={!!editPromptWarning}>
            <Alert severity="warning" sx={{ mt: 1 }}>{editPromptWarning}</Alert>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Abbrechen</Button>
          <Button onClick={handleUpdatePrompt} variant="contained" disabled={editPromptContent.length > 300}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Prompt löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diesen Prompt löschen möchten? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>



    </Box>
  );
};

export default Settings;
